import { DEFAULT_MODEL_PARAMETERS } from 'config/riskEngine';
import dayjs from 'dayjs';
import { OptionType } from 'types';
import {
  PositionLeg,
  PositionShares,
  IVAdjustmentSettings,
} from 'types/optionsCalculator';
import { TableData } from 'types/riskEngine';
import { getProfitLoss } from './riskEngine';

export const shortIdGenerator = () => {
  return Math.random().toString(36).substring(2, 8);
};

// Format: SYM_YYMMDD_TYPE_STRIKE_QTY_PRICE_ID
// Example: SPY_250620_C_560_1_12.35_abc123
export const stringifyLeg = (leg: PositionLeg): string => {
  const typeChar = leg.type === OptionType.CALL ? 'C' : 'P';
  const dateStr = dayjs(leg.expiration).format('YYMMDD');
  return `${leg.sym}_${dateStr}_${typeChar}_${leg.strike}_${leg.quantity}_${leg.price}_${leg.id}`;
};

export const stringifyShare = (share: PositionShares): string => {
  return `${share.sym}_${share.quantity}_${share.pricePaid}`;
};

export const parseLegString = (legStr: string): PositionLeg | null => {
  const parts = legStr.split('_');
  if (parts.length !== 7) {
    return null;
  }

  const [sym, dateStr, typeChar, strikeStr, qtyStr, priceStr, id] = parts;

  // Convert YYMMDD to correct milliseconds since epoch
  const expiration = dayjs(`20${dateStr}`, 'YYYYMMDD')
    .tz('America/New_York')
    .hour(16)
    .minute(0)
    .second(0)
    .millisecond(0)
    .valueOf();

  return {
    id,
    sym,
    quantity: parseInt(qtyStr, 10),
    strike: parseFloat(strikeStr),
    expiration,
    type: typeChar === 'C' ? OptionType.CALL : OptionType.PUT,
    price: parseFloat(priceStr),
  };
};

export const parseShareString = (shareStr: string): PositionShares | null => {
  const parts = shareStr.split('_');
  if (parts.length !== 3) {
    return null;
  }

  const [sym, size, price] = parts;

  return {
    sym,
    quantity: parseInt(size, 10),
    pricePaid: parseFloat(price),
  };
};

export const stringifyIVSettings = (
  expiration: number,
  settings: IVAdjustmentSettings,
): string => {
  const { levelShift, callSlopeMultiplier, putSlopeMultiplier } = settings;
  return `${expiration}:${levelShift}:${callSlopeMultiplier}:${putSlopeMultiplier}`;
};

export const parseIVSettings = (
  str: string,
): [number, IVAdjustmentSettings] | null => {
  const parts = str.split(':');
  if (parts.length !== 4) return null;

  const [expiration, levelShift, callSlopeMultiplier, putSlopeMultiplier] =
    parts;
  return [
    parseInt(expiration, 10),
    {
      levelShift: parseFloat(levelShift),
      callSlopeMultiplier: parseFloat(callSlopeMultiplier),
      putSlopeMultiplier: parseFloat(putSlopeMultiplier),
    },
  ];
};

export function getAdjustedIV(
  initialIV: number,
  minIV: number,
  adjustmentParams: IVAdjustmentSettings,
  isCall: boolean,
) {
  // Apply IV adjustments based on the parameters
  let adjustedIV = initialIV;

  // Apply slope multiplier (steepen/flatten)
  adjustedIV =
    minIV +
    (adjustedIV - minIV) *
      (isCall
        ? adjustmentParams.callSlopeMultiplier
        : adjustmentParams.putSlopeMultiplier);

  // Apply level shift (parallel shift)
  adjustedIV += adjustmentParams.levelShift;

  // Ensure IV stays positive
  adjustedIV = Math.max(0.01, adjustedIV);

  return adjustedIV;
}

export function getAdjustedVolData(
  initialVolData: TableData[],
  expirationsIVSettingsMap: Map<number, IVAdjustmentSettings>,
  optionType: OptionType,
): TableData[] {
  // Calculate minimum IV once
  const minIV = Math.min(
    ...initialVolData.flatMap((data) => Object.values(data.strikePrices)),
  );

  return initialVolData.map((data) => {
    const ivSettings = expirationsIVSettingsMap.get(data.tte);
    if (!ivSettings) return data;

    // Process strike prices in one pass
    const strikePrices = Object.entries(data.strikePrices).reduce(
      (acc, [strike, iv]) => {
        acc[Number(strike)] = getAdjustedIV(
          iv,
          minIV,
          ivSettings,
          optionType === OptionType.CALL,
        );
        return acc;
      },
      {} as Record<number, number>,
    );

    return { ...data, strikePrices };
  });
}

export const getOptionsCalculatorProfitLoss = (
  legs: PositionLeg[],
  shares: PositionShares[],
  volData: TableData[],
) => {
  return getProfitLoss(legs, shares, volData, {
    ...DEFAULT_MODEL_PARAMETERS,
    volatility: undefined,
  });
};

export const formatStrikeIndicatorExpiration = (expiration: number) => {
  const expirationDate = dayjs(expiration);
  const currentYear = dayjs().year();
  const expirationYear = expirationDate.year();

  if (expirationYear > currentYear) {
    return expirationDate.format('YYYY-M-D');
  }

  return expirationDate.format('M-D');
};
